import { type FC, useEffect, useRef, useState } from "react";

import { SideSheet } from "../../common/SideSheet";
import {
  type ActorAttribution,
  type BlackboardInstance,
} from "../blackboardModels";
import { type OrgChannelSummary } from "../useOrgChannels";
import { ActorSheet } from "./ActorSheet";
import { ChannelSheet } from "./ChannelSheet";
import {
  InspectorToolbar,
  NotFoundSheet,
  SHEET_WIDTH,
  type BreadcrumbItem,
} from "./InspectorChrome";
import { type ChannelInspectorState } from "./useChannelInspectorState";

// ---------------------------------------------------------------------------
// InspectorSheet — thin dispatcher over the two stable inspector identities.
//
// Base slot: the actor sheet (or the channel sheet when opened directly from
// the fleet header — no actor context).
// Overlay slot: a channel sheet stacked above the actor sheet, opened from
// the actor's pivot/workspace. Closing the overlay returns to the base with
// all its state intact — no re-mode. The overlay's Esc is gated so only the
// topmost sheet dismisses; browser-back handles base/overlay via the caller's
// URL state.
// ---------------------------------------------------------------------------

interface InspectorSheetProps {
  actorAttributions: ActorAttribution[];
  isOpen: boolean;
  /** The shared channel under inspection (`?inspect=channel:`). */
  channelId: string | null;
  /** The actor under inspection (`?inspect=actor:`). Exactly one of
   *  channelId/actorId is set at a time. */
  actorId: string | null;
  /** A channel opened as overlay above the actor (`?inspect=actor:<id>` +
   *  `&overlay=channel:<id>`). Only meaningful when actorId is set. */
  overlayChannelId: string | null;
  /** An actor opened as overlay above the channel base (`?inspect=channel:` +
   *  `&overlay=actor:<id>`) — the presence-rail drill-in. */
  overlayActorId: string | null;
  onClose: () => void;
  onCloseOverlay: () => void;
  onInspectChannel: (channelId: string) => void;
  onInspectActor: (instanceId: string) => void;
  ancestors: BreadcrumbItem[];
  onJumpTo: (index: number) => void;
  instances: BlackboardInstance[];
  orgChannelsById: Map<string, OrgChannelSummary>;
  orgChannelsLoading: boolean;
  /** Refetch the org channel list. Used as a one-shot grace retry before
   *  declaring an inspected channel "not found" — a just-created channel can
   *  be navigated to before the realtime reload has landed. MUST resolve true
   *  only when the list was actually refreshed: a false (fetch failed) result
   *  is not confirmation of absence and must not produce "not found". */
  onReloadOrgChannels?: () => Promise<boolean>;
  recentlySpawnedInstanceIds: ReadonlySet<string>;
  generatingInstances: ReadonlySet<string>;
  sendMessage: (
    instanceId: string,
    channelId: string,
    message: string,
  ) => Promise<string>;
  onKillInstance: (instanceId: string) => Promise<void>;
  onRefresh: () => void;
  /** Shared per-channel persistence (drafts/tabs/artifacts). Lifted out so the
   *  base and overlay slots share the same channel state when a channel moves
   *  between them. */
  channelState: ChannelInspectorState;
}

export const InspectorSheet: FC<InspectorSheetProps> = ({
  actorAttributions,
  isOpen,
  channelId,
  actorId,
  overlayChannelId,
  overlayActorId,
  onClose,
  onCloseOverlay,
  onInspectChannel,
  onInspectActor,
  ancestors,
  onJumpTo,
  instances,
  orgChannelsById,
  orgChannelsLoading,
  onReloadOrgChannels,
  recentlySpawnedInstanceIds,
  generatingInstances,
  sendMessage,
  onKillInstance,
  onRefresh,
  channelState,
}) => {
  // Grace retry before "channel not found": a just-created channel can be
  // inspected before the org channel list has refetched (the realtime
  // channel_changed reload is debounced), so an unknown id gets one imperative
  // reload before NotFound may render. Two separate memories:
  //   - attempted (ref): loop guard. One reload per inspected id, so a failing
  //     endpoint isn't hammered.
  //   - confirmed (state): only a reload that actually REFRESHED the list
  //     confirms absence. A failed refetch (transport error) leaves the id
  //     unconfirmed, so the channel sheet keeps rendering instead of lying
  //     "not found".
  // Both are pruned to the currently-inspected ids, so navigating away and
  // back retries an id whose reload failed.
  const missRetriedIdsRef = useRef<Set<string>>(new Set());
  const [graceReloadIds, setGraceReloadIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [confirmedMissIds, setConfirmedMissIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useEffect(() => {
    const inspected = [channelId, overlayChannelId].filter(
      (id): id is string => id !== null,
    );
    for (const id of missRetriedIdsRef.current) {
      if (!inspected.includes(id)) {
        missRetriedIdsRef.current.delete(id);
      }
    }
    setConfirmedMissIds((prev) => pruneToInspected(prev, inspected));
    if (!onReloadOrgChannels) {
      return;
    }
    const missing = planGraceReloads(
      inspected,
      orgChannelsById,
      orgChannelsLoading,
      missRetriedIdsRef.current,
    );
    if (missing.length === 0) {
      return;
    }
    for (const id of missing) {
      missRetriedIdsRef.current.add(id);
    }
    setGraceReloadIds((prev) => new Set([...prev, ...missing]));
    const settle = (refreshed: boolean) => {
      // Batched together: NotFound is gated on `confirmed`, so clearing the
      // in-flight marker never exposes an unconfirmed frame.
      setGraceReloadIds((prev) => {
        const next = new Set(prev);
        for (const id of missing) {
          next.delete(id);
        }
        return next;
      });
      if (refreshed) {
        setConfirmedMissIds((prev) => new Set([...prev, ...missing]));
      }
    };
    void Promise.resolve(onReloadOrgChannels()).then(
      (refreshed) => {
        settle(refreshed);
      },
      () => {
        settle(false);
      },
    );
  }, [
    channelId,
    overlayChannelId,
    orgChannelsById,
    orgChannelsLoading,
    onReloadOrgChannels,
  ]);
  const channelNotFound = (id: string): boolean =>
    shouldShowChannelNotFound(id, orgChannelsById, orgChannelsLoading, {
      canReload: onReloadOrgChannels !== undefined,
      confirmedMissIds,
      graceReloadIds,
    });

  if (!isOpen) {
    return null;
  }

  // --- Base slot -----------------------------------------------------------
  // The actor sheet is the base when inspecting an actor. The channel sheet
  // is the base when opened directly from the fleet header (no actor context).
  let baseSheet: React.ReactNode = null;
  if (actorId) {
    const instance = instances.find((inst) => inst.id === actorId) ?? null;
    if (instance) {
      baseSheet = (
        // Keyed by actor so switching actors remounts the sheet — otherwise
        // uncommitted field state (identity prompt, overrides) from actor A
        // survives the switch and a save would write it onto actor B.
        <ActorSheet
          key={instance.id}
          isOpen
          instance={instance}
          isGenerating={generatingInstances.has(instance.id)}
          onClose={onClose}
          ancestors={ancestors}
          onJumpTo={onJumpTo}
          onInspectChannel={onInspectChannel}
          onKillInstance={onKillInstance}
          onRefresh={onRefresh}
          escDismisses={overlayChannelId === null}
          zIndexClass="z-50"
        />
      );
    } else if (recentlySpawnedInstanceIds.has(actorId)) {
      baseSheet = (
        <MaterializingActorSheet
          isOpen
          onClose={onClose}
          ancestors={ancestors}
          onJumpTo={onJumpTo}
        />
      );
    } else {
      baseSheet = (
        <NotFoundSheet
          isOpen
          onClose={onClose}
          ancestors={ancestors}
          onJumpTo={onJumpTo}
          label="Actor not found"
          sub="This actor no longer exists or is not visible."
        />
      );
    }
  } else if (channelId) {
    baseSheet = channelNotFound(channelId) ? (
      <NotFoundSheet
        isOpen
        onClose={onClose}
        ancestors={ancestors}
        onJumpTo={onJumpTo}
        label="Channel not found"
        sub="This channel no longer exists or is not visible."
      />
    ) : (
      <ChannelSheet
        key={channelId}
        actorAttributions={actorAttributions}
        isOpen
        channelId={channelId}
        onClose={onClose}
        ancestors={ancestors}
        onJumpTo={onJumpTo}
        onInspectActor={onInspectActor}
        instances={instances}
        orgChannelsById={orgChannelsById}
        onReloadOrgChannels={onReloadOrgChannels}
        sendMessage={sendMessage}
        onKillInstance={onKillInstance}
        onRefresh={onRefresh}
        draft={channelState.draftFor(channelId)}
        onDraftChange={channelState.setDraft}
        activeTab={channelState.tabFor(channelId)}
        onActiveTabChange={channelState.setTab}
        selectedArtifactId={channelState.artifactFor(channelId)}
        onSelectedArtifactChange={channelState.setArtifact}
        escDismisses={overlayActorId === null}
        zIndexClass="z-50"
        closeLabel="Close"
      />
    );
  }

  // --- Overlay slot --------------------------------------------------------
  // A channel sheet stacked above the actor base. Opened from the actor's
  // pivot/workspace. Closing it (Esc, backdrop, ‹ Close) returns to the base.
  let overlaySheet: React.ReactNode = null;
  if (actorId && overlayChannelId) {
    overlaySheet = channelNotFound(overlayChannelId) ? (
      <NotFoundSheet
        isOpen
        onClose={onCloseOverlay}
        ancestors={[]}
        onJumpTo={() => {
          /* Overlay has no breadcrumb */
        }}
        label="Channel not found"
        sub="This channel no longer exists or is not visible."
      />
    ) : (
      <ChannelSheet
        key={overlayChannelId}
        actorAttributions={actorAttributions}
        isOpen
        channelId={overlayChannelId}
        onClose={onCloseOverlay}
        ancestors={[]}
        onJumpTo={() => {
          /* Overlay has no breadcrumb */
        }}
        onInspectActor={onInspectActor}
        instances={instances}
        orgChannelsById={orgChannelsById}
        onReloadOrgChannels={onReloadOrgChannels}
        sendMessage={sendMessage}
        onKillInstance={onKillInstance}
        onRefresh={onRefresh}
        draft={channelState.draftFor(overlayChannelId)}
        onDraftChange={channelState.setDraft}
        activeTab={channelState.tabFor(overlayChannelId)}
        onActiveTabChange={channelState.setTab}
        selectedArtifactId={channelState.artifactFor(overlayChannelId)}
        onSelectedArtifactChange={channelState.setArtifact}
        escDismisses
        zIndexClass="z-[60]"
        closeLabel="‹ Back"
      />
    );
  }

  // An actor drilled into from the channel's presence rail stacks above the
  // channel base — visible immediately, ‹ Back pops to the channel intact.
  if (channelId && overlayActorId) {
    const overlayInstance =
      instances.find((inst) => inst.id === overlayActorId) ?? null;
    overlaySheet = overlayInstance ? (
      <ActorSheet
        isOpen
        instance={overlayInstance}
        isGenerating={generatingInstances.has(overlayInstance.id)}
        onClose={onCloseOverlay}
        ancestors={[]}
        onJumpTo={() => {
          /* Overlay has no breadcrumb */
        }}
        onInspectChannel={onInspectChannel}
        onKillInstance={onKillInstance}
        onRefresh={onRefresh}
        escDismisses
        zIndexClass="z-[60]"
        closeLabel="‹ Back"
      />
    ) : (
      <NotFoundSheet
        isOpen
        onClose={onCloseOverlay}
        ancestors={[]}
        onJumpTo={() => {
          /* Overlay has no breadcrumb */
        }}
        label="Actor not found"
        sub="This actor no longer exists or is not visible."
      />
    );
  }

  return (
    <>
      {baseSheet}
      {overlaySheet}
    </>
  );
};

export function isMissingLoadedChannel(
  channelId: string,
  orgChannelsById: ReadonlyMap<string, OrgChannelSummary>,
  orgChannelsLoading: boolean,
): boolean {
  return !orgChannelsLoading && !orgChannelsById.has(channelId);
}

/** Inspected ids that are missing from the loaded list and have not yet had
 *  their one-shot grace reload — the set the effect should refetch for. */
export function planGraceReloads(
  inspectedIds: readonly string[],
  orgChannelsById: ReadonlyMap<string, OrgChannelSummary>,
  orgChannelsLoading: boolean,
  retriedIds: ReadonlySet<string>,
): string[] {
  return inspectedIds.filter(
    (id) =>
      isMissingLoadedChannel(id, orgChannelsById, orgChannelsLoading) &&
      !retriedIds.has(id),
  );
}

/** NotFound renders only when the id is missing, no grace reload is in
 *  flight, and either a grace reload SUCCEEDED (confirming absence) or no
 *  reload capability was provided. A reload that failed confirms nothing, so
 *  it must not appear in `confirmedMissIds`. */
export function shouldShowChannelNotFound(
  channelId: string,
  orgChannelsById: ReadonlyMap<string, OrgChannelSummary>,
  orgChannelsLoading: boolean,
  { graceReloadIds, confirmedMissIds, canReload }: ChannelNotFoundReloadState,
): boolean {
  return (
    isMissingLoadedChannel(channelId, orgChannelsById, orgChannelsLoading) &&
    !graceReloadIds.has(channelId) &&
    (confirmedMissIds.has(channelId) || !canReload)
  );
}

interface ChannelNotFoundReloadState {
  graceReloadIds: ReadonlySet<string>;
  confirmedMissIds: ReadonlySet<string>;
  canReload: boolean;
}

/** Drops ids no longer inspected, preserving identity when nothing changed so
 *  the state update is a no-op (the effect calls this on every run). */
export function pruneToInspected(
  ids: ReadonlySet<string>,
  inspectedIds: readonly string[],
): ReadonlySet<string> {
  const kept = [...ids].filter((id) => inspectedIds.includes(id));
  return kept.length === ids.size ? ids : new Set(kept);
}

const MaterializingActorSheet: FC<{
  isOpen: boolean;
  onClose: () => void;
  ancestors: BreadcrumbItem[];
  onJumpTo: (index: number) => void;
}> = ({ isOpen, onClose, ancestors, onJumpTo }) => (
  <SideSheet
    isOpen={isOpen}
    onClose={onClose}
    title="Materializing actor"
    widthClass={SHEET_WIDTH}
    headerContent={
      <div className="flex min-w-0 items-center gap-3">
        <div className="h-10 w-10 animate-pulse bg-slate-200 dark:bg-slate-800" />
        <div className="min-w-0 space-y-1.5">
          <div className="h-3 w-36 animate-pulse bg-slate-200 dark:bg-slate-800" />
          <div className="h-2 w-24 animate-pulse bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    }
  >
    <div className="-mx-3 -my-2.5 flex h-[calc(100%+1.25rem)] flex-col sm:-mx-4 sm:-my-3 sm:h-[calc(100%+1.5rem)]">
      <InspectorToolbar
        ancestors={ancestors}
        currentLabel="materializing"
        onJumpTo={onJumpTo}
      />
      <div className="min-h-0 flex-1 p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-500">
          materializing…
        </p>
      </div>
    </div>
  </SideSheet>
);
